import React, { useState, useEffect } from "react";
import {
  Box,
  Avatar,
  Paper,
  CircularProgress,
  Alert,
  IconButton,
} from "@mui/material";
import CountryCodePhoneField from "../components/CountryCodePhoneField";
import { useNavigate } from "react-router-dom";
import CustomLabel from "../components/CustomLabel";
import CustomTextField from "../components/CustomTextField";
import CustomButton from "../components/CustomButton";
import { COLORS } from "../assets/theme";
import GenericFormDialog from "../components/GenericFormDialog";
import { getUserById, updateClient } from "../services/UsersService";
import { changePassword, getUserId } from "../services/authService";
import { useUser } from "../contexts/UserContext";
import DeleteIcon from "@mui/icons-material/Delete";
import { toast } from "react-toastify";

const MAX_IMAGE_SIZE_BYTES = 2 * 1024 * 1024;
const initialProfile = {
  fullName: "John Doe",
  userName: "johndoe",
  email: "john@example.com",
  password: "password123",
  address: "123 Main St, City, Country",
  phone: "",
  country_code: "",
  mobile: "",
  image: "",
};

const mapUserToProfile = (user: any) => {
  const phone = user?.mobile
    ? `${user?.country_code ? `${user.country_code} ` : ""}${user.mobile}`
    : "";
  return {
    fullName: user?.name || "",
    userName: user?.name || "",
    email: user?.email || "",
    password: "********",
    address: user?.address || "",
    phone,
    country_code: user?.country_code || "",
    mobile: user?.mobile || "",
    image: user?.banner || "",
  };
};

const Profile: React.FC = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(initialProfile);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState(initialProfile);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [removeImage, setRemoveImage] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  const { user: ctxUser, setUser } = useUser();

  useEffect(() => {
    if (ctxUser) {
      const userData = mapUserToProfile(ctxUser);
      setProfile(userData);
      setForm(userData);
      setLoading(false);
      return;
    }
    const fetchUserProfile = async () => {
      try {
        setLoading(true);
        setError(null);
        const userId = getUserId();

        if (!userId) {
          setError("User ID not found. Please login again.");
          setLoading(false);
          return;
        }

        const response = await getUserById(userId);

        if (response && response.user) {
          const userData = mapUserToProfile(response.user);

          setProfile(userData);
          setForm(userData);
          if (setUser) setUser(response.user);
        }
      } catch (err: any) {
        console.error("Error fetching user profile:", err);
        setError(err?.response?.data?.message || "Failed to load profile data");
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, []);

  const DEFAULT_IMAGE = "/";

  const passwordFields = [
    {
      type: "password" as const,
      name: "oldPassword",
      label: "Old Password",
      required: true,
      showPassword: true,
    },
    {
      type: "password" as const,
      name: "newPassword",
      label: "New Password",
      required: true,
      showPassword: true,
    },
    {
      type: "password" as const,
      name: "confirmPassword",
      label: "Confirm Password",
      required: true,
      showPassword: true,
    },
  ];

  const handleChangePassword = () => {
    setPasswordDialogOpen(true);
  };

  const handlePasswordDialogSubmit = async (values: Record<string, string>) => {
    if (changingPassword) return;
    const currentPassword = (values.oldPassword || "").trim();
    const newPassword = (values.newPassword || "").trim();
    const confirmPassword = (values.confirmPassword || "").trim();

    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error("Please fill in all password fields.");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("New password and confirmation do not match.");
      return;
    }

    setChangingPassword(true);
    try {
      const response = await changePassword(
        currentPassword,
        newPassword,
        confirmPassword
      );
      if (!response.success) {
        toast.error(response.error || "Failed to change password.");
        return;
      }
      toast.success(response.message || "Password updated successfully.");
      setPasswordDialogOpen(false);
    } catch (err: any) {
      const apiErrorMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message;
      toast.error(apiErrorMessage || "Failed to change password.");
    } finally {
      setChangingPassword(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleEdit = () => {
    setEditMode(true);
  };

  const handleCancelEdit = () => {
    try {
      const maybeFile = (form as any).imageFile;
      const maybeImage = (form as any).image;
      if (maybeFile instanceof File && typeof maybeImage === "string") {
        if (maybeImage.startsWith("blob:")) {
          URL.revokeObjectURL(maybeImage);
        }
      }
    } catch (e) {
      // ignore
    }

    setForm((prev) => ({ ...(profile as any) } as typeof prev));

    try {
      if (setUser && profile) {
        setUser((prev: any) => ({ ...(prev || {}), banner: profile.image }));
      }
    } catch (e) {
      // noop
    }

    setEditMode(false);
    setRemoveImage(false);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setSaveSuccess(false);
      const userId = getUserId();

      if (!userId) {
        setError("User ID not found. Please login again.");
        setSaving(false);
        return;
      }

      const formData = new FormData();
      formData.append("name", form.fullName);
      formData.append("email", form.email);
      // prefer explicit `mobile` field and country code
      const mobileRaw = (form as any).mobile || (form as any).phone || "";
      formData.append("mobile", String(mobileRaw).replace(/\D/g, ""));
      formData.append("country_code", (form as any).country_code || "");
      formData.append("address", form.address);
      if ((form as any).imageFile instanceof File) {
        formData.append("banner", (form as any).imageFile);
        formData.append("files", (form as any).imageFile);
      }
      if (removeImage) {
        formData.append("remove_banner", "true");
        formData.append("banner", "");
      }

      try {
        for (const pair of (formData as any).entries()) {
          console.debug("Profile update: form entry ->", pair[0], pair[1]);
        }
      } catch (e) {
        // noop
      }

      const response = await updateClient(userId, formData);

      if (response && response.user) {
        const updatedProfile = mapUserToProfile(response.user);
        setProfile(updatedProfile);
        setForm((prev) => ({
          ...prev,
          ...updatedProfile,
          imageFile: undefined,
        }));
        setRemoveImage(false);
        if (setUser)
          setUser((prev: any) => ({
            ...(response.user || {}),
            banner: response.user?.banner || DEFAULT_IMAGE,
          }));
      } else {
        const fallbackProfile = {
          ...profile,
          ...form,
          password: "********",
          image: removeImage
            ? DEFAULT_IMAGE
            : (form as any).image ?? profile.image,
        };
        setProfile(fallbackProfile);
        setForm((prev) => ({
          ...prev,
          ...fallbackProfile,
          imageFile: undefined,
        }));
        if (setUser)
          setUser((prev: any) => ({
            ...(prev || {}),
            banner: fallbackProfile.image,
          }));
      }

      setEditMode(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err: any) {
      console.error("Error saving profile:", err);
      setError(err?.response?.data?.message || "Failed to save profile");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: { xs: "80vh", sm: "80vh" },
        px: { xs: 1, sm: 0 },
        bgcolor: { xs: COLORS.WhiteBackground, sm: "transparent" },
      }}
    >
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress sx={{ color: COLORS.MainColor }} />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ maxWidth: 500 }}>
          {error}
        </Alert>
      ) : (
        <Paper
          elevation={4}
          sx={{
            p: 4,
            borderRadius: 4,
            minWidth: 350,
            maxWidth: 500,
            width: "100%",
            bgcolor: COLORS.WhiteBackground,
            border: {
              xs: `1px solid ${COLORS.MainColor}`,
              sm: `2px solid ${COLORS.MainColor}`,
            },
            boxSizing: "border-box",
          }}
        >
          {saveSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Profile updated successfully!
            </Alert>
          )}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mb: { xs: 2, sm: 3 },
            }}
          >
            <Box sx={{ position: "relative", mb: { xs: 1, sm: 2 } }}>
              <Avatar
                src={editMode ? profile.image : profile.image}
                sx={{
                  width: { xs: 80, sm: 100 },
                  height: { xs: 70, sm: 100 },
                  cursor: editMode ? "pointer" : "default",
                  boxShadow: 2,
                  border: `2px solid ${COLORS.MainColor}`,
                }}
                onClick={() => {
                  if (editMode) {
                    document.getElementById("profile-image-input")?.click();
                  }
                }}
              />
              {editMode && profile.image && profile.image !== DEFAULT_IMAGE && (
                <IconButton
                  aria-label="remove-image"
                  sx={{
                    position: "absolute",
                    right: -6,
                    top: -6,
                    bgcolor: "white",
                    border: `1px solid ${COLORS.MainColor}`,
                    width: 28,
                    height: 28,
                    "&:hover": { bgcolor: "rgba(0,0,0,0.06)" },
                  }}
                  onClick={() => {
                    try {
                      const maybeImage = (form as any).image;
                      if (
                        maybeImage &&
                        typeof maybeImage === "string" &&
                        maybeImage.startsWith("blob:")
                      ) {
                        URL.revokeObjectURL(maybeImage);
                      }
                    } catch (e) {
                      // noop
                    }

                    setProfile((prev) => ({ ...prev, image: DEFAULT_IMAGE }));
                    setForm((prev) => ({
                      ...prev,
                      image: DEFAULT_IMAGE,
                      imageFile: undefined,
                    }));
                    setRemoveImage(true);

                    if (setUser) {
                      try {
                        setUser((prev: any) => ({
                          ...(prev || {}),
                          banner: DEFAULT_IMAGE,
                        }));
                      } catch (e) {
                        // noop
                      }
                    }
                  }}
                >
                  <DeleteIcon sx={{ fontSize: 16, color: COLORS.MainColor }} />
                </IconButton>
              )}
              <input
                id="profile-image-input"
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    if (file.size > MAX_IMAGE_SIZE_BYTES) {
                      toast.error("Image must be 2MB or smaller.");
                      e.target.value = "";
                      return;
                    }
                    setRemoveImage(false);
                    const imagePreview =
                      file && typeof file === "object" && "name" in file
                        ? URL.createObjectURL(file)
                        : form.image || "/favicon.ico";

                    setProfile((prev) => ({
                      ...prev,
                      image: imagePreview,
                    }));
                    setForm((prev) => ({
                      ...prev,
                      imageFile: file,
                      image: imagePreview,
                    }));
                    if (setUser) {
                      try {
                        setUser((prev: any) => ({
                          ...(prev || {}),
                          banner: imagePreview,
                        }));
                      } catch (e) {
                        // noop
                      }
                    }
                  }
                }}
              />
            </Box>
            <CustomLabel
              text="Profile"
              fontWeight={700}
              fontSize="2rem"
              color={COLORS.MainColor}
              sx={{
                textAlign: "center",
                fontSize: { xs: "1.5rem", sm: "2rem" },
              }}
            />
          </Box>

          <Box
            component="form"
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: { xs: 1.5, sm: 2 },
            }}
          >
            <CustomTextField
              label="Full Name"
              name="fullName"
              value={editMode ? form.fullName : profile.fullName}
              onChange={handleChange}
              disabled={!editMode}
              fullWidth
              sx={{
                fontSize: { xs: "0.95rem", sm: "1rem" },
                backgroundColor: COLORS.textFieldBackgroundColor,
              }}
            />
            <CustomTextField
              label="Email"
              name="email"
              value={editMode ? form.email : profile.email}
              onChange={handleChange}
              disabled
              fullWidth
              sx={{
                fontSize: { xs: "0.95rem", sm: "1rem" },
                backgroundColor: COLORS.textFieldBackgroundColor,
              }}
            />
            <Box
              sx={{
                display: { xs: "block", sm: "flex" },
                alignItems: { sm: "center" },
                gap: { sm: 2 },
              }}
            >
              <CustomTextField
                label="Password"
                name="password"
                type="password"
                value={editMode ? form.password : profile.password}
                onChange={handleChange}
                disabled
                fullWidth
                sx={{
                  mb: { xs: 1, sm: 0 },
                  backgroundColor: COLORS.textFieldBackgroundColor,
                }}
              />
              <CustomButton
                label="Change Password"
                disabled={!editMode}
                variant="contained"
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  borderRadius: 999,
                  fontWeight: 400,
                  bgcolor: COLORS.MainColor,
                  color: COLORS.WhiteBackground,
                  boxShadow: 3,
                  letterSpacing: 1,
                  fontSize: { xs: "0.7rem", sm: "0.5rem" },
                  transition: "all 0.2s",
                  mt: { xs: 0, sm: 2 },
                  "&:hover": {
                    bgcolor: COLORS.MainColor,
                    opacity: 0.85,
                    boxShadow: 6,
                    transform: "scale(1.02)",
                  },
                }}
                onClick={handleChangePassword}
              />
            </Box>
            <GenericFormDialog
              open={passwordDialogOpen}
              title="Change Password"
              onClose={() => setPasswordDialogOpen(false)}
              onSubmit={handlePasswordDialogSubmit}
              fields={passwordFields}
              submitLabel={changingPassword ? "Submitting..." : "Submit"}
            />
            {/* <CustomTextField
              label="Address"
              name="address"
              value={editMode ? form.address : profile.address}
              onChange={handleChange}
              disabled={!editMode}
              fullWidth
              sx={{
                fontSize: { xs: "0.95rem", sm: "1rem" },
                backgroundColor: COLORS.textFieldBackgroundColor,
              }}
            /> */}
            <CountryCodePhoneField
              countryCode={
                editMode
                  ? (form as any).country_code || ""
                  : profile.country_code
              }
              phoneNumber={
                editMode ? (form as any).mobile || "" : profile.mobile
              }
              onCountryCodeChange={(value) =>
                setForm((prev) => ({ ...prev, country_code: value }))
              }
              onPhoneNumberChange={(value) =>
                setForm((prev) => ({ ...prev, mobile: value }))
              }
              error={false}
              disabled={!editMode}
              compact={!editMode}
            />
            {!editMode ? (
              <Box sx={{ display: "flex", gap: 2, mt: { xs: 1.5, sm: 2 } }}>
                <CustomButton
                  label="Edit"
                  variant="contained"
                  sx={{
                    flex: 1,
                    fontSize: { xs: "0.95rem", sm: "1rem" },
                  }}
                  onClick={handleEdit}
                />
                <CustomButton
                  label="Cancel"
                  variant="outlined"
                  sx={{
                    flex: 1,
                    fontSize: { xs: "0.95rem", sm: "1rem" },
                    borderColor: COLORS.MainColor,
                    color: COLORS.MainColor,
                    "&:hover": {
                      borderColor: COLORS.MainColor,
                      bgcolor: "rgba(0, 0, 0, 0.04)",
                    },
                  }}
                  onClick={() => navigate(-1)}
                />
              </Box>
            ) : (
              <Box sx={{ display: "flex", gap: 2, mt: { xs: 1.5, sm: 2 } }}>
                <CustomButton
                  label="Save"
                  variant="contained"
                  disabled={saving}
                  sx={{
                    flex: 1,
                    bgcolor: "success.main",
                    fontSize: { xs: "0.95rem", sm: "1rem" },
                  }}
                  onClick={handleSave}
                />
                <CustomButton
                  label="Cancel"
                  variant="outlined"
                  sx={{
                    flex: 1,
                    fontSize: { xs: "0.95rem", sm: "1rem" },
                    borderColor: COLORS.MainColor,
                    color: COLORS.MainColor,
                    "&:hover": {
                      borderColor: COLORS.MainColor,
                      bgcolor: "rgba(207, 27, 27, 0.04)",
                    },
                  }}
                  onClick={handleCancelEdit}
                />
              </Box>
            )}
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default Profile;
